import type { ComponentPropsWithRef, ReactNode } from 'react';
import { cn } from '../../lib/cn.ts';
import { Card } from '../card/card.tsx';

export type ScrollCardProps = Omit<
  ComponentPropsWithRef<'section'>,
  'style' | 'title'
> & {
  /** Stays put while the body scrolls under it. */
  title: ReactNode;
};

/*
 * Styling the WebKit scrollbar is what keeps it on screen: macOS otherwise
 * draws an overlay bar that only appears while scrolling, and a cut-off text
 * gives no other sign that there is more. Firefox cannot be kept from hiding
 * it, so it only gets the thin, quiet version.
 */
const visibleScrollbar = [
  '[&::-webkit-scrollbar]:w-1.5',
  '[&::-webkit-scrollbar-track]:bg-transparent',
  '[&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-ink-faint',
  'supports-[not_selector(::-webkit-scrollbar)]:[scrollbar-width:thin]',
  'supports-[not_selector(::-webkit-scrollbar)]:[scrollbar-color:var(--color-ink-faint)_transparent]',
];

/*
 * A card whose title stays put while its body scrolls. Only scrolls when its
 * parent bounds its height (a stretched flex item, a fixed height); otherwise
 * it is as tall as what is in it.
 */
export function ScrollCard({ title, className, children, ...props }: ScrollCardProps) {
  return (
    <Card
      as='section'
      padding='none'
      className={cn('flex min-h-0 min-w-0 flex-col', className)}
      {...props}
    >
      <div data-slot='scroll-card-title' className='shrink-0 px-5.5 pt-5.5 pb-3'>
        {title}
      </div>
      {/* Focusable, so the text can be scrolled from the keyboard too. */}
      <div
        data-slot='scroll-card-body'
        tabIndex={0}
        className={cn(
          'min-h-0 flex-1 overflow-y-auto px-5.5 pb-5.5',
          'rounded-b-lg focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ink-faint',
          visibleScrollbar,
        )}
      >
        {children}
      </div>
    </Card>
  );
}
